<template>
    <div id="function-list">
        <FunctionEditor v-if="createMode" @close="createMode = false" @save="saveFunction" createMode />
        <template v-else>
            <FunctionEditor
                v-if="selectedFunction"
                v-model="selectedFunction.aiFunction"
                @close="selectedFunction = null"
                :story-id="storyId"
                @deleted="() => onDeletedSingle(selectedFunction._id)"
                @save="updateFunction"
            />
            <div class="fn-container" v-else-if="!selectedFunction">
                <div class="header">
                    <div class="title">
                        {{ $t("chatbotv2.aiFunctions") }}
                            <i 
                            class="material-icons info-icon" 
                            v-tippy="{ 
                                content: $t('chatbotv2.aiFunctionCreateHint'),
                                placement: 'bottom',
                                theme: 'light'
                            }"
                        >
                            info
                        </i>
                    </div>
                    <div class="actions">
                        <SupsisButton primary @click="createMode = true">
                            <i class="material-icons">add</i>
                            <span>{{ $t("chatbotv2.addFunction") }}</span>
                        </SupsisButton>
                    </div>
                </div>
                <div>
                    <ve-pagination
                        :total="total"
                        :page-index="page"
                        :page-size="pageSize"
                        :page-size-option="[10, 15, 20, 50, 100]"
                        @on-page-number-change="(val) => (page = val)"
                        @on-page-size-change="(val) => (pageSize = val)"
                    />
                </div>
                <div class="content">
                    <div class="function-table">
                        <div class="function-table-header">
                            <div
                                class="function-table-header-item"
                                v-for="column in columns"
                                :key="column.id"
                                :style="{
                                    flex: column.flex,
                                    width: column.width ? column.width + 'px' : 'auto',
                                    'max-width': column.maxWidth ? column.maxWidth + 'px' : 'auto',
                                }"
                            >
                                <a-checkbox
                                    v-if="column.isCheckbox"
                                    @click.stop="toggleSelectAll"
                                    :checked="isSelectAll()"
                                />
                                <template v-else>{{ column.title }}</template>
                            </div>
                        </div>
                        <div class="function-table-body">
                            <div class="row" v-for="row in filteredRows" :key="row._id">
                                <div
                                    class="cell"
                                    v-for="column in columns"
                                    :key="column.id"
                                    :style="{
                                        flex: column.flex,
                                        width: column.width ? column.width + 'px' : 'auto',
                                        'max-width': column.maxWidth ? column.maxWidth + 'px' : 'auto',
                                    }"
                                    @click="previewArticles(row)"
                                >
                                    <template v-if="column.slot === 'state'">
                                        <div class="d-flex w-100">
                                            <div v-if="row.enabled" class="state-cell active">
                                                {{ $t("chatbotv2.usedByAi") }}
                                            </div>
                                            <div v-else class="state-cell passive">{{ $t("chatbotv2.notUsed") }}</div>
                                        </div>
                                    </template>
                                    <template v-else>
                                        <template v-if="column.isCheckbox">
                                            <a-checkbox :checked="isSelected(row)" @click.stop="() => toggleSelect(row)" />
                                        </template>
                                        <template v-else>
                                            <span v-if="column.getData">{{ column.getData(row) }}</span>
                                            <span v-else>{{ row[column.id] }}</span>
                                        </template>
                                    </template>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Ön Tanımlı Fonksiyonlar Bölümü -->
                    <div class="built-in-functions-section">
                        <h2>{{ $t("chatbotv2.builtInFunctionsTitle") }}</h2>
                        <p class="section-description">
                            {{ $t("chatbotv2.builtInFunctionsDesc") }}
                        </p>

                        <div class="built-in-functions-list">
                            <FeatureItem
                                :title="$t('chatbotv2.builtInFunctionsOptionButtonsTitle')"
                                icon="ChatAiIcon"
                                v-model="showOptionButtonsEnabled"
                                :description="$t('chatbotv2.builtInFunctionsOptionButtonsDesc')"
                                type="switch"
                            />
                            <FeatureItem
                                :title="$t('chatbotv2.builtInFunctionsCloseConversationTitle')"
                                icon="ChatAiIcon"
                                v-model="closeConversationEnabled"
                                :description="$t('chatbotv2.builtInFunctionsCloseConversationDesc')"
                                type="switch"
                            />
                            <FeatureItem
                                :title="$t('chatbotv2.builtInFunctionsTransferChatTitle')"
                                icon="ChatAiIcon"
                                v-model="transferChatEnabled"
                                :description="$t('chatbotv2.builtInFunctionsTransferChatDesc')"
                                type="switch"
                            />
                        </div>
                    </div>

                    <div class="select-panel-wrapper" v-if="selectedItems.length">
                        <div class="select-panel">
                            <div class="items-count">
                                <i class="material-icons spin" v-if="busy">"autorenew"</i>
                                <i class="material-icons" v-else>check</i>
                                <div>{{ selectedItems.length }} {{ $t("auto.chatbotSelectedCountSuffix") }}</div>
                            </div>
                            <div class="buttons">
                                <SupsisButton small :disabled="busy" @click="() => deleteMulti()"
                                    ><i class="material-icons">delete</i>
                                </SupsisButton>
                                <SupsisButton small :disabled="busy"
                                    ><i class="material-icons">refresh</i></SupsisButton
                                >
                                <SupsisButton :disabled="busy" @click="() => multiChangeState(false)">
                                    <i class="material-icons">toggle_off</i>
                                    {{ $t("chatbotv2.disableAi") }}
                                </SupsisButton>
                                <SupsisButton :disabled="busy" @click="() => multiChangeState(true)">
                                    <i class="material-icons">toggle_on</i>
                                    {{ $t("chatbotv2.enableAi") }}
                                </SupsisButton>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>
<script>
import { mapActions, mapState } from "vuex";
import ComingSoon from "../ComingSoon.vue";
import SupsisButton from "../../../Settings/components/SupsisButton.vue";
import FeatureItem from "../../../Settings/components/FeatureItem.vue";
import { CHATBOT_API } from "../../../../../api/chatbotv2";
import moment from "moment";
import ContentPreview from "../ContentPreview.vue";
import { sendSocketMessage } from "../../../../../utils/socketStream";
import FunctionEditor from "./FunctionEditor.vue";

export default {
    name: "FunctionList",
    components: {
        FunctionEditor,
        ContentPreview,
        SupsisButton,
        ComingSoon,
        FeatureItem,
    },
    props: {
        storyId: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            total: 0,
            page: 1,
            pageSize: 10,
            selectedFunction: null,
            createMode: false,
            selectedItems: [],
            columns: [
                {
                    title: "",
                    id: "checkbox",
                    isCheckbox: true,
                    width: 50,
                    maxWidth: 50,
                },
                {
                    title: this.$t("message.name"),
                    id: "aiFunction.name",
                    flex: 2,
                    getData: (row) => {
                        return row.aiFunction.name;
                    },
                },
                {
                    title: this.$t("message.description"),
                    id: "aiFunction.description",
                    flex: 2,
                    getData: (row) => {
                        return row.aiFunction.description;
                    },
                },
                {
                    title: this.$t("chatbotv2.parameterCount"),
                    id: "aiFunction.parameters",
                    flex: 2,
                    getData: (row) => {
                        return row.aiFunction.parameters.length;
                    },
                },
                {
                    title: this.$t("message.createdAt"),
                    id: "createdAt",
                    getData: (row) => {
                        return moment(row.createdAt).format("DD/MM/YYYY");
                    },
                },
                {
                    title: this.$t("chatbotv2.state"),
                    id: "state",
                    slot: "state",
                },
            ],
            rows: [],
            loading: true,
            busy: false,
            showOptionButtonsEnabled: false,
            closeConversationEnabled: false,
            transferChatEnabled: false,
        };
    },
    computed: {
        ...mapState({
            site: (state) => state.site.site,
            stories: (state) => state.CB.stories,
        }),
        filteredRows() {
            return this.rows.filter((row) => !!row.aiFunction);
        },
    },
    methods: {
        ...mapActions({
            updateStorySingleKey: "CB/updateStorySingleKey",
        }),
        async fetchStorySettings() {
            const story = this.stories.find((s) => s._id === this.storyId);
            if (story && story.ai) {
                this.showOptionButtonsEnabled = Array.isArray(story.ai.enabledFunctions)
                    ? story.ai.enabledFunctions.includes("show_option_buttons")
                    : false;
                this.closeConversationEnabled = Array.isArray(story.ai.enabledFunctions)
                    ? story.ai.enabledFunctions.includes("close_chat")
                    : false;
                this.transferChatEnabled = Array.isArray(story.ai.enabledFunctions)
                    ? story.ai.enabledFunctions.includes("transfer_chat")
                    : false;
            }
        },
        async toggleShowOptionButtons(checked) {
            this.showOptionButtonsEnabled = checked;

            let enabledFunctions = [];
            const story = this.stories.find((s) => s._id === this.storyId);

            if (story && story.ai && Array.isArray(story.ai.enabledFunctions)) {
                enabledFunctions = [...(story.ai.enabledFunctions || [])];
            }

            if (this.showOptionButtonsEnabled) {
                if (!enabledFunctions.includes("show_option_buttons")) {
                    enabledFunctions.push("show_option_buttons");
                }
            } else {
                enabledFunctions = enabledFunctions.filter((fn) => fn !== "show_option_buttons");
            }

            try {
                await this.updateStorySingleKey({
                    id: this.storyId,
                    key: "ai.enabledFunctions",
                    value: enabledFunctions,
                });

                this.$message.success(
                    this.showOptionButtonsEnabled
                        ? "Yapay zeka butonlu mesaj gönderebilir fonksiyonu etkinleştirildi"
                        : "Yapay zeka butonlu mesaj gönderebilir fonksiyonu devre dışı bırakıldı"
                );

                this.$emit("updated");
            } catch (error) {
                console.error("Error updating enabled functions:", error);
                this.$message.error("Ayarı güncellenirken bir hata oluştu");
            }
        },
        async toggleCloseChat(checked) {
            this.closeConversationEnabled = checked;

            let enabledFunctions = [];
            const story = this.stories.find((s) => s._id === this.storyId);

            if (story && story.ai && Array.isArray(story.ai.enabledFunctions)) {
                enabledFunctions = [...(story.ai.enabledFunctions || [])];
            }

            if (this.closeConversationEnabled) {
                if (!enabledFunctions.includes("close_chat")) {
                    enabledFunctions.push("close_chat");
                }
            } else {
                enabledFunctions = enabledFunctions.filter((fn) => fn !== "close_chat");
            }

            try {
                await this.updateStorySingleKey({
                    id: this.storyId,
                    key: "ai.enabledFunctions",
                    value: enabledFunctions,
                });

                this.$message.success(
                    this.closeConversationEnabled
                        ? "Yapay zeka görüşmeyi sonlandırabilir fonksiyonu etkinleştirildi"
                        : "Yapay zeka görüşmeyi sonlandırabilir fonksiyonu devre dışı bırakıldı"
                );

                this.$emit("updated");
            } catch (error) {
                console.error("Error updating enabled functions:", error);
                this.$message.error("Ayarı güncellenirken bir hata oluştu");
            }
        },
        async toggleTransferChat(checked) {
            this.transferChatEnabled = checked;

            let enabledFunctions = [];
            const story = this.stories.find((s) => s._id === this.storyId);

            if (story && story.ai && Array.isArray(story.ai.enabledFunctions)) {
                enabledFunctions = [...(story.ai.enabledFunctions || [])];
            }

            if (this.transferChatEnabled) {
                if (!enabledFunctions.includes("transfer_chat")) {
                    enabledFunctions.push("transfer_chat");
                }
            } else {
                enabledFunctions = enabledFunctions.filter((fn) => fn !== "transfer_chat");
            }

            try {
                await this.updateStorySingleKey({
                    id: this.storyId,
                    key: "ai.enabledFunctions",
                    value: enabledFunctions,
                });

                this.$message.success(
                    this.transferChatEnabled
                        ? "Yapay zeka canlı desteğe aktarabilir fonksiyonu etkinleştirildi"
                        : "Yapay zeka canlı desteğe aktarabilir fonksiyonu devre dışı bırakıldı"
                );

                this.$emit("updated");
            } catch (error) {
                console.error("Error updating enabled functions:", error);
                this.$message.error("Ayarı güncellenirken bir hata oluştu");
            }
        },
        openAiAdded() {
            sendSocketMessage("chatbot/v2 kb-function-added", {
                siteId: this.site._id,
                storyId: this.storyId,
                isAssistantAPI: !!this.$store.state.CB.stories.find((story) => story._id === this.storyId)?.ai
                    ?.assistantId,
            });
        },
        openAiRemoved() {
            sendSocketMessage("chatbot/v2 kb-function-removed", {
                siteId: this.site._id,
                storyId: this.storyId,
                isAssistantAPI: !!this.$store.state.CB.stories.find((story) => story._id === this.storyId)?.ai
                    ?.assistantId,
            });
        },
        selectAll() {
            this.selectedItems = this.rows;
        },
        deselectAll() {
            this.selectedItems = [];
        },
        isSelectAll() {
            return this.selectedItems.length === this.rows.length;
        },
        toggleSelectAll() {
            if (this.selectedItems.length === this.rows.length) {
                this.deselectAll();
            } else {
                this.selectAll();
            }
        },
        toggleSelect(item) {
            const isSelected = this.selectedItems.find((i) => i._id === item._id);
            if (isSelected) {
                this.selectedItems = this.selectedItems.filter((i) => i._id !== item._id);
            } else {
                this.selectedItems.push(item);
            }
        },
        isSelected(item) {
            return !!this.selectedItems.find((i) => i._id === item._id);
        },
        reTrain() {
            sendSocketMessage("chatbot/v2 complete-train", {
                siteId: this.siteId,
                storyId: this.storyId,
                pageIds: [],
                isFunction: true,
                isAssistantAPI: !!this.$store.state.CB.stories.find((story) => story._id === this.storyId)?.ai
                    ?.assistantId,
            });
        },
        fetch(page, pageSize) {
            this.loading = true;
            CHATBOT_API.KNOWLEDGE_BASE.listKnowledgeBase(this.storyId, "aiFunction", page, pageSize).then((res) => {
                this.rows = res.data.rows;
                this.total = res.data.total;
                this.loading = false;
            });
        },
        onDeletedSingle(id) {
            this.busy = true;
            const hide = this.$message.loading("Function is being deleted...", 0);
            CHATBOT_API.KNOWLEDGE_BASE.deleteMultiKnowledgeBase(this.storyId, [id])
                .then((isSuccess) => {
                    if (!isSuccess) return Promise.reject("Failed to delete the function.");
                    this.$message.success("AI Function is deleted.");
                    this.rows = this.rows.filter((i) => i._id !== id);
                    this.selectedFunction = null;
                    this.selectedItems = [];
                    this.reTrain();
                    this.$emit("trained");
                    this.openAiRemoved();
                    this.openAiAdded();
                })
                .catch((error) => {
                    this.$message.error("Function is not deleted. Please try again.");
                })
                .finally(() => {
                    this.busy = false;
                    hide();
                });
        },
        onChangedStateSingle(id, enabled) {
            this.$message.success(enabled ? "Selected functions are enabled." : "Selected functions are disabled.");
            this.rows = this.rows.map((x) => {
                if (x._id === id) {
                    x.enabled = enabled;
                }
                return x;
            });
            this.selectedItems = [];
            this.reTrain();
            this.$emit("trained");
            this.selectedFunction = null;
        },
        deleteMulti() {
            const selectedIds = this.selectedItems.map((i) => i._id);
            this.busy = true;
            const hide = this.$message.loading("Selected functions are being deleted...", 0);
            CHATBOT_API.KNOWLEDGE_BASE.deleteMultiKnowledgeBase(this.storyId, selectedIds)
                .then((isSuccess) => {
                    if (!isSuccess) return Promise.reject("Failed to change state.");
                    this.$message.success("Selected functions are deleted.");
                    this.selectedItems = [];
                    this.reTrain();
                    this.rows = this.rows.filter((i) => !selectedIds.includes(i._id));
                    this.$emit("trained");
                    this.openAiRemoved();
                    this.openAiAdded();
                    this.$emit("updated");
                })
                .catch((error) => {
                    this.$message.error("Selected functions are not deleted. Please try again.");
                })
                .finally(() => {
                    this.busy = false;
                    hide();
                });
        },
        multiChangeState(enabled) {
            const selectedIds = this.selectedItems.map((i) => i._id);
            this.busy = true;
            const hide = this.$message.loading(
                enabled ? "Selected functions are being enabled..." : "Selected functions are being disabled...",
                0
            );
            CHATBOT_API.KNOWLEDGE_BASE.changeStateMultiKnowledgeBase(this.storyId, selectedIds, enabled)
                .then((isSuccess) => {
                    if (!isSuccess) return Promise.reject("Failed to change state.");
                    this.$message.success(
                        enabled ? "Selected functions are enabled." : "Selected functions are disabled."
                    );
                    this.selectedItems = [];
                    this.reTrain();
                    this.fetch();
                    this.$emit("trained");
                    this.openAiRemoved();
                    this.openAiAdded();
                    this.$emit("updated");
                })
                .catch((error) => {
                    this.$message.error(
                        enabled ? "Selected functions are not enabled." : "Selected functions are not disabled."
                    );
                })
                .finally(() => {
                    this.busy = false;
                    hide();
                });
        },
        async saveFunction(payload) {
            const kb = await CHATBOT_API.KNOWLEDGE_BASE.createKnowledgeBase(
                this.storyId,
                "aiFunction",
                undefined,
                undefined,
                undefined,
                undefined,
                {
                    ...payload,
                }
            ).then((res) => res.data);
            this.rows.push(kb);
            this.$emit("updated");
            if (this.rows.length > 0) {
                this.openAiRemoved();
            }
            this.openAiAdded();
            this.$message.success("AI Function is created.");
            this.createMode = false;
        },
        async updateFunction(payload) {
            console.log("updateFunction", payload);
            await CHATBOT_API.KNOWLEDGE_BASE.knowledgeBaseUpdate(this.storyId, this.selectedFunction._id, payload)
                .then((res) => res.data)
                .then((data) => {
                    this.rows = this.rows.map((row) => {
                        if (row._id === this.selectedFunction._id) row = data;
                        return row;
                    });
                    this.selectedFunction = null;
                    this.$emit("updated");
                });
            this.openAiRemoved();
            this.openAiAdded();
        },
        previewArticles(row) {
            this.selectedFunction = row;
            this.createMode = false;
        },
    },
    mounted() {
        this.fetch();
        this.fetchStorySettings();
    },
    watch: {
        page() {
            this.fetch(this.page, this.pageSize);
        },
        pageSize() {
            this.fetch(this.page, this.pageSize);
        },
        storyId: {
            handler() {
                this.fetchStorySettings();
            },
            immediate: true,
        },
        showOptionButtonsEnabled: {
            handler() {
                const enabledFunctions = this.stories.find((s) => s._id === this.storyId)?.ai?.enabledFunctions || [];
                const isChanged = this.showOptionButtonsEnabled
                    ? !enabledFunctions.includes("show_option_buttons")
                    : enabledFunctions.includes("show_option_buttons");
                if (isChanged) {
                    this.toggleShowOptionButtons(this.showOptionButtonsEnabled);
                }
            },
            immediate: true,
        },
        closeConversationEnabled: {
            handler() {
                const enabledFunctions = this.stories.find((s) => s._id === this.storyId)?.ai?.enabledFunctions || [];
                const isChanged = this.closeConversationEnabled
                    ? !enabledFunctions.includes("close_chat")
                    : enabledFunctions.includes("close_chat");
                if (isChanged) {
                    this.toggleCloseChat(this.closeConversationEnabled);
                }
            },
            immediate: true,
        },
        transferChatEnabled: {
            handler() {
                const enabledFunctions = this.stories.find((s) => s._id === this.storyId)?.ai?.enabledFunctions || [];
                const isChanged = this.transferChatEnabled
                    ? !enabledFunctions.includes("transfer_chat")
                    : enabledFunctions.includes("transfer_chat");
                if (isChanged) {
                    this.toggleTransferChat(this.transferChatEnabled);
                }
            },
            immediate: true,
        },
    },
};
</script>
<style lang="scss" scoped>
#function-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 10px;
    width: 100%;
    height: 100%;

    .fn-container {
        .header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 20px 0px;
            width: 100%;

            .title {
                color: #22272d;
                display: flex;
                font-size: 22px;
                font-weight: 600;
                line-height: 28px;
                align-items: center;

                .info-icon {
                    margin-left: 8px;
                    font-size: 18px;
                    color: #79797c;
                    cursor: pointer;
                    transition: color 0.3s ease;

                    &:hover {
                        color: #22272d;
                    }
                }
            }

            .actions {
                display: flex;
                align-items: center;
                gap: 15px;
            }
        }

        .content {
            display: flex;
            flex-direction: column;
            gap: 10px;
            padding: 20px;

            .built-in-functions-section {
                background: #fff;
                border-radius: 10px;
                box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.05);
                padding: 20px;
                margin-top: 20px;

                h2 {
                    font-size: 18px;
                    font-weight: 600;
                    color: #22272d;
                    margin-bottom: 10px;
                }

                .section-description {
                    font-size: 14px;
                    color: #79797c;
                    margin-bottom: 20px;
                }

                .built-in-functions-list {
                    display: flex;
                    flex-direction: column;
                    gap: 15px;

                    :deep(.feature) {
                        width: 100%;
                        max-width: 100%;

                        .left {
                            .icon {
                                svg {
                                    width: 40px;
                                    height: 40px;
                                }
                            }
                        }
                    }
                }
            }
        }

        .function-table {
            width: 100%;

            .function-table-header {
                display: flex;
                align-items: center;
                width: 100%;
                padding: 10px;
                background-color: #f6f6f6;
                border-radius: 5px;
                font-weight: 600;

                .function-table-header-item {
                    flex: 1;
                    padding: 0 10px;
                }
            }

            .function-table-body {
                display: flex;
                flex-direction: column;
                gap: 10px;
                width: 100%;
                align-items: center;
                overflow-y: auto;
                height: 100%;
                max-height: calc(100vh - 100px);

                .row {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    width: 100%;
                    padding: 10px;
                    border-radius: 5px;
                    background: var(--color-bg-neutral-default);
                    cursor: pointer;

                    &:hover {
                        opacity: 0.8;
                    }

                    .cell {
                        flex: 1;
                        color: #000;
                        font-size: 15px;
                        font-weight: 600;
                        line-height: 19px;
                        overflow: hidden !important;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                        padding: 0 10px;
                    }

                    .state-cell {
                        display: flex;
                        align-items: center;
                        font-size: 14px;
                        font-weight: 600;
                        height: 32px;
                        justify-content: center;
                        line-height: 18px;
                        padding: 0 6px;
                        border-radius: 10px;

                        &.active {
                            background: #fff;
                            color: #00893c;
                            width: min-content;
                        }

                        &.passive {
                            background: #dcdcdc;
                            color: #79797c;
                            width: min-content;
                        }
                    }
                }
            }
        }

        .select-panel-wrapper {
            bottom: 10px;
            left: 36px;
            position: absolute;
            right: 36px;

            .select-panel {
                background: #22272d;
                border-radius: 15px;
                box-shadow: 0 -4px 32px 0 rgba(0, 0, 0, 0.16);
                color: #fff;
                height: 64px;
                align-items: center;
                display: flex;
                justify-content: center;

                .items-count {
                    align-items: center;
                    display: flex;
                    justify-content: center;
                    font-size: 16px;
                    font-weight: 600;
                    line-height: 20px;
                    margin-left: 16px;
                }

                .buttons {
                    align-items: center;
                    display: flex;
                    gap: 8px;
                    justify-content: center;
                    margin-left: auto;
                    margin-right: 16px;
                }
            }
        }
    }
}
</style>
